/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */


import { useMemo, useState } from 'react';
import Utils from '../common/utils';

/**
 * Reusable hook for regex-based searching/filtering.
 * @param {Array<any>} data
 * @param {{ searchKeys?: string[] }} options
 */

export default function useSearch(data, options = {}) {
  const { searchKeys = [], strictKeys = false } = options;
  const [search, setSearch] = useState('');

  const regex = useMemo(() => Utils.buildRegex(search), [search]);

  const getSearchableString = useMemo(() => (item) => {
    if (Array.isArray(searchKeys) && searchKeys.length > 0) {
      return searchKeys
        .map((key) => {
          const value = item && item[key];
          if (Array.isArray(value)) return value.join(' ');
          if (value && typeof value === 'object') return Object.values(value).join(' ');

          return value != null ? String(value) : '';
        })
        .join(' ');
    }
    if (strictKeys) return '';
    if (item && typeof item === 'object') {
      return Object.values(item)
        .map((v) => {
          if (Array.isArray(v)) return v.join(' ');
          if (v && typeof v === 'object') return Object.values(v).join(' ');

          return v != null ? String(v) : '';
        })
        .join(' ');
    }

    return String(item || '');
  }, [searchKeys]);

  const filteredData = useMemo(() => {
    if (!regex) return data || [];

    return (data || []).filter((row) => regex.test(getSearchableString(row)));
  }, [data, regex, getSearchableString]);

  return {
    search,
    setSearch,
    regex,
    filteredData,
    getSearchableString
  };
}

